import React from 'react';
import PropTypes from 'prop-types';

import { App } from '@meronex/app';

import { toast, ToastContainer } from 'react-toastify';
import PWAPrompt from 'react-ios-pwa-prompt';

import IConvert from '@meronex/icons/mdc/MdcTapeMeasure';
import IUser from '@meronex/icons/fi/FiUser';
import ISun from '@meronex/icons/fi/FiSun';
import IMoon from '@meronex/icons/fi/FiMoon';
import IMenu from '@meronex/icons/fi/FiMenu';
import IClose from '@meronex/icons/fi/FiX';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import PulseIndicator from '../../meronex-app/components/core/PulseIndicator';
import SettingsDialog from '../../meronex-app/layout/settings/SettingsDialog';

import View from './components/View';
import UnitConverter from './customViews/unitConverter/UnitConverter';

import Home from '../home/Home';
import SideMenu from './components/SideMenu';

import ConfigurationsView from '../configurations/ConfigurationsView';
import EquipmentsView from '../equipments/EquipmentsView';

import StoresView from '../stores/StoresView';
import SalesView from '../sales/SalesView';
import CalculatorView from './customViews/CalculatorView';
import NewsView from './customViews/news/NewsView';

import FactoriesView from '../factories/FactoriesView';
import LogsView from '../logs/LogsView';
import IssuesView from '../issues/IssuesView';

function ViewsContainer(props) {
  const [activeView, setActiveView] = React.useState('home');

  const onActiveViewEvent = (e) => {
    setActiveView(e.activeView);
  };
  React.useEffect(() => {
    App.eventsManager.on(
      'event-active-view',
      ViewsContainer.name,
      onActiveViewEvent
    );
    return () => {
      App.eventsManager.removeListener(
        'event-active-view',
        ViewsContainer.name,
        onActiveViewEvent
      );
    };
  }, []);

  const onWorkOrderAdded = () => {
    App.eventsManager.emit('event-work-order-added', ViewsContainer.name);
  };

  const bodyStyle = {};

  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const isDesktop = useMediaQuery('(min-width:1280px)');
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    setUser(App.getUser());
    App.eventsManager.on('event-user-loaded', 'ViewsContainer', () => {
      setUser(App.getUser());
    });
    return () => {
      App.eventsManager.removeListener('event-user-loaded', 'ViewsContainer');
    };
  }, []);

  const userName = user && user.profile
    ? user.profile.firstName || user.profile.name || user.username || 'Profile'
    : 'Profile';
  const avatarUrl = App.utils.getUserAvatar(user);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Close mobile menu when navigating
  React.useEffect(() => {
    if (!isDesktop) {
      setMobileMenuOpen(false);
    }
  }, [activeView]);

  // Listen for mobile menu open/close events
  React.useEffect(() => {
    App.eventsManager.on('event-open-mobile-menu', 'ViewsContainer', () => {
      setMobileMenuOpen(true);
    });
    App.eventsManager.on('event-close-mobile-menu', 'ViewsContainer', () => {
      setMobileMenuOpen(false);
    });
    return () => {
      App.eventsManager.removeListener('event-open-mobile-menu', 'ViewsContainer');
      App.eventsManager.removeListener('event-close-mobile-menu', 'ViewsContainer');
    };
  }, []);

  // Touch-tracking drawer — follows finger, snaps open/closed
  const DRAWER_WIDTH = 280;
  const drawerRef = React.useRef(null);
  const overlayRef = React.useRef(null);
  const touchStartX = React.useRef(null);
  const touchCurrentX = React.useRef(null);
  const isDragging = React.useRef(false);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    isDragging.current = false;
    // Disable transition during drag for instant response
    if (drawerRef.current) drawerRef.current.style.transition = 'none';
    if (overlayRef.current) overlayRef.current.style.transition = 'none';
  };

  const onTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchStartX.current - touchCurrentX.current;
    if (diff > 10) isDragging.current = true;
    if (!isDragging.current) return;

    // Clamp: only allow dragging left (closing direction)
    const offset = Math.max(0, Math.min(diff, DRAWER_WIDTH));
    if (drawerRef.current) {
      drawerRef.current.style.transform = `translateX(-${offset}px)`;
    }
    if (overlayRef.current) {
      overlayRef.current.style.opacity = 1 - (offset / DRAWER_WIDTH);
    }
  };

  const onTouchEnd = () => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - (touchCurrentX.current || touchStartX.current);

    // Re-enable transition for snap animation
    if (drawerRef.current) drawerRef.current.style.transition = 'transform 0.25s ease-out';
    if (overlayRef.current) overlayRef.current.style.transition = 'opacity 0.25s ease-out';

    if (diff > 80) {
      // Swipe past threshold — close with animation
      if (drawerRef.current) drawerRef.current.style.transform = `translateX(-100%)`;
      if (overlayRef.current) overlayRef.current.style.opacity = 0;
      setTimeout(() => setMobileMenuOpen(false), 250);
    } else {
      // Snap back open
      if (drawerRef.current) drawerRef.current.style.transform = 'translateX(0)';
      if (overlayRef.current) overlayRef.current.style.opacity = 1;
    }

    touchStartX.current = null;
    touchCurrentX.current = null;
    isDragging.current = false;
  };

  // Close with slide-out animation (for overlay tap and menu item clicks)
  const closeDrawer = () => {
    if (drawerRef.current) {
      drawerRef.current.style.transition = 'transform 0.25s ease-out';
      drawerRef.current.style.transform = 'translateX(-100%)';
    }
    if (overlayRef.current) {
      overlayRef.current.style.transition = 'opacity 0.25s ease-out';
      overlayRef.current.style.opacity = 0;
    }
    setTimeout(() => setMobileMenuOpen(false), 250);
  };

  const onLogoClick = () => {
    if (App.eventsManager.hasListeners('event-open-about')) {
      App.eventsManager.emit('event-open-about', 'ViewsContainer');
    } else {
      window.location.reload(true);
    }
  };

  const sidebarContent = (
    <>
      {/* Logo area */}
      <div
        onClick={onLogoClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '0 20px',
          height: '68px',
          cursor: 'pointer',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
          flexShrink: 0,
        }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S</span>
        </div>
        <div>
          <span style={{
            fontWeight: 600,
            fontSize: '18px',
            color: isDark ? '#e2e8f0' : '#1e293b',
          }}>
            SitesHUB
          </span>
          <div style={{
            fontSize: '10px',
            color: isDark ? '#64748b' : '#94a3b8',
            marginTop: '-2px',
          }}>
            v{App.metadata?.version} · b{App.metadata?.build}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, paddingLeft: '16px', paddingRight: '12px', overflowY: 'auto' }}>
        <SideMenu activeView={activeView} />
      </div>
      {/* Footer — user, theme, pulse */}
      <div style={{
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
          padding: '14px 16px',
          paddingBottom: 'max(14px, env(safe-area-inset-bottom))',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexShrink: 0,
        }}>
          <div
            onClick={() => { setSettingsOpen(true); setMobileMenuOpen(false); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flex: 1,
              cursor: 'pointer',
              minWidth: 0,
            }}>
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={userName}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <IUser size={16} style={{ color: '#fff' }} />
              </div>
            )}
            <span style={{
              fontSize: '14px',
              fontWeight: 500,
              color: isDark ? '#e2e8f0' : '#475569',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {userName}
            </span>
          </div>
          {!App.opts.disablePulseIndicator && <PulseIndicator />}
          <div
            onClick={() => {
              App.themeManager.changeTheme(isDark ? 'light' : 'dark');
            }}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: isDark ? '#fbbf24' : '#64748b',
              flexShrink: 0,
            }}>
            {isDark ? <ISun size={14} /> : <IMoon size={14} />}
          </div>
        </div>
    </>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', overflow: 'hidden' }}>
      {/* Desktop sidebar — always visible */}
      {isDesktop && (
        <div style={{
          width: '270px',
          minWidth: '270px',
          overflowY: 'auto',
          backgroundColor: isDark ? '#1e1e22' : '#fff',
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
          boxSizing: 'border-box',
        }}>
          {sidebarContent}
        </div>
      )}

      {/* Mobile drawer overlay */}
      {!isDesktop && mobileMenuOpen && (
        <div
          ref={overlayRef}
          onClick={closeDrawer}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
            backgroundColor: 'rgba(0,0,0,0.5)',
            animation: 'fadeIn 0.2s ease-out',
          }}>
          <div
            ref={drawerRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: `${DRAWER_WIDTH}px`,
              height: '100%',
              backgroundColor: isDark ? '#1e1e22' : '#fff',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
              animation: 'slideIn 0.2s ease-out',
              willChange: 'transform',
            }}>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}>
          {/* Mobile header bar */}
          {!isDesktop && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              height: '56px',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: isDark ? '#1e1e22' : '#fff',
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
            }}>
              <div
                onClick={() => setMobileMenuOpen(true)}
                style={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: isDark ? '#e2e8f0' : '#334155',
                }}>
                <IMenu size={22} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: isDark ? '#e2e8f0' : '#1e293b',
                }}>
                  SitesHUB
                </span>
                <div style={{
                  fontSize: '10px',
                  color: isDark ? '#64748b' : '#94a3b8',
                  marginTop: '-2px',
                }}>
                  v{App.metadata?.version} · b{App.metadata?.build}
                </div>
              </div>
              <div style={{ width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {!App.opts.disablePulseIndicator && <PulseIndicator />}
              </div>
            </div>
          )}

          <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
          <Home activeView={activeView} />
          <SalesView activeView={activeView} />
          <StoresView activeView={activeView} />
          <FactoriesView
            activeView={activeView}
            viewId={'factories'}
            onWorkOrderAdded={onWorkOrderAdded}
          />
          <EquipmentsView activeView={activeView} />
          <ConfigurationsView activeView={activeView} />
          <IssuesView activeView={activeView} />
          <LogsView activeView={activeView} />
          <View
            viewId={'unitsConverter'}
            activeView={activeView}
            title={'Units Converter'}
            icon={<IConvert size={20} />}
            showMenu>
            <UnitConverter />
          </View>
      </div>
      <PWAPrompt
        promptOnVisit={1}
        timesToShow={7}
        delay={5000}
        permanentlyHideOnDismiss={false}
      />
      <ToastContainer
        autoClose={1750}
        position={App.getToastPosition() || toast.POSITION.BOTTOM_CENTER}
      />
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
ViewsContainer.propTypes = {
  showHeader: PropTypes.bool,
};

ViewsContainer.defaultProps = {
  showHeader: true,
};

export default ViewsContainer;
